import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  Globe, Plus, Pencil, Trash2, Tag, X,
  ArrowLeft, Loader2, Users, Check, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';

const GEO_DEFAULT_TAGS = 'Business, Politics, Crime, Community, Development';

const emptyForm = {
  name: '', domain: '', site_type: 'geo',
  hub_name: '', region: '', industry_category: '', tagline: '',
  story_tags_csv: GEO_DEFAULT_TAGS, industry_tags_csv: '',
  local_areas_csv: '', user_access: 'all', user_ids: [],
};


// ─── Tags Tab ───

function TagsTab({ pub, allTags, onTagsChange }) {
  const [newTagName, setNewTagName] = useState('');
  const [saving, setSaving]         = useState(false);
  const pubTags = allTags.filter(t => t.publication_id === pub.id);

  const handleAdd = async () => {
    if (!newTagName.trim()) return;
    setSaving(true);
    try {
      await api.post('/tags', { name: newTagName.trim(), publication_id: pub.id });
      toast.success('Tag added');
      setNewTagName('');
      onTagsChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add tag');
    } finally { setSaving(false); }
  };

  const handleDelete = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success('Tag removed');
      onTagsChange();
    } catch { toast.error('Failed to remove tag'); }
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Editorial tags for story classification. Changes here automatically update the site navigation.
      </p>
      <div className="flex flex-wrap gap-2 min-h-[40px]">
        {pubTags.length === 0
          ? <p className="text-xs text-muted-foreground italic">No tags yet</p>
          : pubTags.map(tag => (
            <Badge key={tag.id} className="bg-[#FFF4E8] text-[#6E6258] border-[#E7D2B8] gap-1 pr-1 text-xs">
              {tag.name}
              <button onClick={() => handleDelete(tag.id)} className="ml-1 hover:text-red-500 transition-colors">
                <X size={11} />
              </button>
            </Badge>
          ))
        }
      </div>
      <div className="flex gap-2">
        <Input
          value={newTagName}
          onChange={e => setNewTagName(e.target.value)}
          placeholder="New tag name"
          className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84] flex-1"
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAdd())}
        />
        <Button onClick={handleAdd} disabled={!newTagName.trim() || saving} className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={16} />}
        </Button>
      </div>
    </div>
  );
}


// ─── Local Areas Tab (geo only) ───

function LocalAreasTab({ pub }) {
  const [areas, setAreas]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId]   = useState(null);
  const [editArea, setEditArea]   = useState({});

  const emptyArea = { hub_name: pub.hub_name || '', area_name: '' };
  const [newArea, setNewArea] = useState(emptyArea);

  const fetchAreas = async () => {
    try {
      const res = await api.get('/locations', { params: { publication_id: pub.id } });
      setAreas(res.data || []);
    } catch { setAreas([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAreas(); }, [pub.id]);

  const handleAdd = async () => {
    if (!newArea.area_name.trim() || !newArea.hub_name.trim()) {
      toast.error('Hub name and area name are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/locations', {
        publication_id: pub.id,
        hub_name:       newArea.hub_name.trim(),
        area_name:      newArea.area_name.trim(),
      });
      toast.success('Area added');
      setNewArea(emptyArea);
      fetchAreas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add area');
    } finally { setSaving(false); }
  };

  const startEdit = (area) => {
    setEditingId(area.id);
    setEditArea({ hub_name: area.hub_name || '', area_name: area.area_name });
  };

  const handleSaveEdit = async (areaId) => {
    setSaving(true);
    try {
      await api.put(`/locations/${areaId}`, {
        hub_name:  editArea.hub_name.trim(),
        area_name: editArea.area_name.trim(),
      });
      toast.success('Area updated');
      setEditingId(null);
      fetchAreas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update area');
    } finally { setSaving(false); }
  };

  const handleDelete = async (areaId) => {
    try {
      await api.delete(`/locations/${areaId}`);
      toast.success('Area deleted');
      setDeleteId(null);
      fetchAreas();
    } catch { toast.error('Failed to delete area'); }
  };

  if (loading) return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
      <Loader2 size={14} className="animate-spin" /> Loading areas...
    </div>
  );

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Local areas for this publication. Story auto-tagging scans published story text for exact area name matches.
      </p>

      {areas.length > 0 && (
        <div className="space-y-1">
          {areas.map(area => (
            <div key={area.id}>
              {editingId === area.id ? (
                <div className="flex items-center gap-2 p-2 bg-[#FFF4E8] border border-[#E7D2B8] rounded-lg">
                  <Input
                    value={editArea.hub_name}
                    onChange={e => setEditArea(p => ({ ...p, hub_name: e.target.value }))}
                    placeholder="Hub name"
                    className="bg-white border-border text-[#1F1A17] text-sm h-8 w-36"
                  />
                  <Input
                    value={editArea.area_name}
                    onChange={e => setEditArea(p => ({ ...p, area_name: e.target.value }))}
                    placeholder="Area name"
                    className="bg-white border-border text-[#1F1A17] text-sm h-8 flex-1"
                    onKeyDown={e => e.key === 'Enter' && handleSaveEdit(area.id)}
                  />
                  <Button size="sm" onClick={() => handleSaveEdit(area.id)} disabled={saving}
                    className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4] h-8 px-3">
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}
                    className="text-muted-foreground hover:text-foreground h-8 px-2">
                    <X size={13} />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between px-3 py-2 bg-[#FFF4E8] border border-[#E7D2B8] rounded-lg group">
                  <div className="flex items-center gap-2 min-w-0">
                    <MapPin size={12} className="text-[#9C8E84] shrink-0" />
                    <span className="text-sm font-medium text-foreground">{area.area_name}</span>
                    {area.hub_name && (
                      <span className="text-xs text-muted-foreground">· {area.hub_name}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-white"
                      onClick={() => startEdit(area)}>
                      <Pencil size={12} />
                    </Button>
                    <Button variant="ghost" size="icon"
                      className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50"
                      onClick={() => setDeleteId(area.id)}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {areas.length === 0 && (
        <p className="text-xs text-muted-foreground italic">No local areas yet.</p>
      )}

      {/* Add new */}
      <div className="flex items-end gap-2 pt-2 border-t border-border">
        <div className="w-36">
          <label className="text-xs text-muted-foreground mb-1 block">Hub Name</label>
          <Input
            value={newArea.hub_name}
            onChange={e => setNewArea(p => ({ ...p, hub_name: e.target.value }))}
            placeholder="e.g. NW London"
            className="bg-white border-border text-[#1F1A17] placeholder:text-[#9C8E84] h-8 text-sm"
          />
        </div>
        <div className="flex-1">
          <label className="text-xs text-muted-foreground mb-1 block">Area Name</label>
          <Input
            value={newArea.area_name}
            onChange={e => setNewArea(p => ({ ...p, area_name: e.target.value }))}
            placeholder="e.g. Brent"
            className="bg-white border-border text-[#1F1A17] placeholder:text-[#9C8E84] h-8 text-sm"
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
        </div>
        <Button
          onClick={handleAdd}
          disabled={saving || !newArea.area_name.trim() || !newArea.hub_name.trim()}
          className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4] h-8 px-3"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={14} />}
        </Button>
      </div>

      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-foreground">Delete area?</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This removes the area and unlinks any stories tagged to it. Cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteId(null)} className="text-muted-foreground">Cancel</Button>
            <Button onClick={() => handleDelete(deleteId)} className="bg-red-600 hover:bg-red-700 text-white">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─── Users Tab ───

function UsersTab({ pub, allUsers, onDataChange }) {
  const [saving, setSaving] = useState(null);

  const assignedUsers   = allUsers.filter(u => (u.publication_ids || []).includes(pub.id));
  const unassignedUsers = allUsers.filter(u => !(u.publication_ids || []).includes(pub.id) && u.role !== 'admin');

  const handleToggle = async (userId, currentlyAssigned) => {
    setSaving(userId);
    try {
      if (currentlyAssigned) {
        await api.delete(`/users/${userId}/publications/${pub.id}`);
        toast.success('User removed');
      } else {
        await api.post(`/users/${userId}/publications/${pub.id}`);
        toast.success('User assigned');
      }
      onDataChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally { setSaving(null); }
  };

  const roleColor = {
    editor: 'bg-amber-100 text-amber-700 border-amber-200',
    caster: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    admin:  'bg-blue-100 text-blue-700 border-blue-200',
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Assign users to this publication. Admins have access to all publications by default.
      </p>
      {assignedUsers.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-[#6E6258] uppercase tracking-wide">Assigned</p>
          {assignedUsers.map(u => (
            <div key={u.id} className="flex items-center justify-between px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{u.name}</p>
                <p className="text-xs text-muted-foreground truncate">{u.email}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-2">
                <Badge className={`text-xs border capitalize ${roleColor[u.role]}`}>{u.role}</Badge>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50"
                  onClick={() => handleToggle(u.id, true)} disabled={saving === u.id}>
                  {saving === u.id ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {unassignedUsers.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-[#6E6258] uppercase tracking-wide">
            {assignedUsers.length > 0 ? 'Add more' : 'Available users'}
          </p>
          {unassignedUsers.map(u => (
            <div key={u.id} className="flex items-center justify-between px-3 py-2 bg-[#FFF4E8] border border-[#E7D2B8] rounded-lg">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{u.name}</p>
                <p className="text-xs text-muted-foreground truncate">{u.email}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-2">
                <Badge className={`text-xs border capitalize ${roleColor[u.role]}`}>{u.role}</Badge>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50"
                  onClick={() => handleToggle(u.id, false)} disabled={saving === u.id}>
                  {saving === u.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {assignedUsers.length === 0 && unassignedUsers.length === 0 && (
        <p className="text-xs text-muted-foreground italic">No users available to assign.</p>
      )}
    </div>
  );
}


// ─── Subscribers Tab ───

function SubscribersTab({ pub }) {
  const [subs, setSubs]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pub?.id) return;
    api.get('/newsletter-subscribers', { params: { publication_id: pub.id } })
      .then(res => setSubs(res.data || []))
      .catch(() => setSubs([]))
      .finally(() => setLoading(false));
  }, [pub.id]);

  const fmtDate = (val) => {
    if (!val) return '—';
    return new Date(val).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
      <Loader2 size={14} className="animate-spin" /> Loading subscribers...
    </div>
  );

  if (subs.length === 0) return <p className="text-sm text-muted-foreground italic">No subscribers yet.</p>;

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        {subs.length} subscriber{subs.length !== 1 ? 's' : ''} for {pub.name}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">Name</th>
              <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">Email</th>
              <th className="text-left py-2 pr-4 text-xs font-medium text-muted-foreground">Subscribed</th>
              <th className="text-left py-2 text-xs font-medium text-muted-foreground">Status</th>
            </tr>
          </thead>
          <tbody>
            {subs.map(s => (
              <tr key={s.id} className="border-b border-border last:border-0 hover:bg-[#FFF4E8]">
                <td className="py-2.5 pr-4 text-foreground font-medium">{s.first_name} {s.last_name}</td>
                <td className="py-2.5 pr-4 text-muted-foreground">{s.email}</td>
                <td className="py-2.5 pr-4 text-muted-foreground text-xs">{fmtDate(s.subscribed_at)}</td>
                <td className="py-2.5 text-xs">
                  {s.unsubscribed_at
                    ? <span className="text-red-500">Unsubscribed {fmtDate(s.unsubscribed_at)}</span>
                    : <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                        Active
                      </span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ─── Detail View ───

function PublicationDetail({ pub, allTags, allUsers, canManage, canDelete, onBack, onEdit, onDelete, onDataChange }) {
  const [activeTab, setActiveTab] = useState('details');

  const pubTags       = allTags.filter(t => t.publication_id === pub.id);
  const assignedCount = allUsers.filter(u => (u.publication_ids || []).includes(pub.id)).length;
  const isGeo         = pub.site_type === 'geo';
  const isIndustry    = pub.site_type === 'industry';

  const tabs = [
    { key: 'details',     label: 'Details' },
    { key: 'tags',        label: `Tags${pubTags.length > 0 ? ` (${pubTags.length})` : ''}` },
    ...(isGeo ? [{ key: 'local_areas', label: 'Local Areas' }] : []),
    { key: 'users',       label: `Users${assignedCount > 0 ? ` (${assignedCount})` : ''}` },
    { key: 'subscribers', label: 'Subscribers' },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}
            className="text-muted-foreground hover:text-foreground hover:bg-[#FFF4E8]">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground font-outfit">{pub.name}</h1>
            {pub.domain && (
              <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1">
                <Globe size={12} /> {pub.domain}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <Button variant="outline" size="sm" onClick={onEdit}
              className="border-border text-foreground hover:bg-[#FFF4E8] gap-1.5">
              <Pencil size={14} /> Edit
            </Button>
          )}
          {canDelete && (
            <Button variant="ghost" size="sm" onClick={() => onDelete(pub.id)}
              className="text-red-500 hover:text-red-600 hover:bg-red-50 gap-1.5">
              <Trash2 size={14} /> Delete
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              activeTab === t.key
                ? 'border-[#1F1A17] text-[#1F1A17]'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'details' && (
        <Card className="bg-card border-border">
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
              {[
                { label: 'Publication Name', value: pub.name },
                { label: 'Domain',           value: pub.domain },
                { label: 'Site Type',        value: isIndustry ? 'Industry' : 'Geographical' },
                { label: isIndustry ? 'Industry / Sector' : 'Region', value: isIndustry ? pub.industry_category : pub.region },
                { label: 'Display Name',     value: pub.display_name },
                { label: !isIndustry ? 'Hub Name' : null, value: !isIndustry ? pub.hub_name : null },
              ].filter(r => r.label && r.value).map(({ label, value }) => (
                <div key={label} className="space-y-0.5">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-foreground font-medium">{value}</p>
                </div>
              ))}
              {pub.tagline && (
                <div className="sm:col-span-2 space-y-0.5">
                  <p className="text-xs text-muted-foreground">Tagline</p>
                  <p className="text-foreground font-medium">{pub.tagline}</p>
                </div>
              )}
            </div>
            {pubTags.length > 0 && (
              <div className="pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                  <Tag size={11} /> {pubTags.length} editorial tag{pubTags.length !== 1 ? 's' : ''}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {pubTags.map(tag => (
                    <Badge key={tag.id} className="bg-[#F8EBD9] text-[#6E6258] border-[#E7D2B8] text-xs">{tag.name}</Badge>
                  ))}
                </div>
              </div>
            )}
            <div className="pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Users size={11} />
                {assignedCount > 0
                  ? `${assignedCount} user${assignedCount !== 1 ? 's' : ''} assigned`
                  : 'All approved users have access'
                }
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'tags' && (
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <TagsTab pub={pub} allTags={allTags} onTagsChange={onDataChange} />
          </CardContent>
        </Card>
      )}

      {activeTab === 'local_areas' && isGeo && (
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <LocalAreasTab pub={pub} />
          </CardContent>
        </Card>
      )}

      {activeTab === 'users' && (
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <UsersTab pub={pub} allUsers={allUsers} onDataChange={onDataChange} />
          </CardContent>
        </Card>
      )}

      {activeTab === 'subscribers' && (
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <SubscribersTab pub={pub} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ─── Main Page ───

export default function PublicationsPage() {
  const { user } = useAuth();
  const [pubs, setPubs]         = useState([]);
  const [allTags, setAllTags]   = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing]   = useState(false);
  const [dialog, setDialog]     = useState(false);
  const [form, setForm]         = useState(emptyForm);
  const [saving, setSaving]     = useState(false);
  const [search, setSearch]     = useState('');

  const canManage = ['admin', 'editor'].includes(user?.role);
  const canDelete = user?.role === 'admin';

  const fetchData = async () => {
    try {
      const [pubRes, tagRes, userRes] = await Promise.allSettled([
        api.get('/publications'),
        api.get('/tags'),
        api.get('/users'),
      ]);
      if (pubRes.status  === 'fulfilled') setPubs(pubRes.value.data  || []);
      if (tagRes.status  === 'fulfilled') setAllTags(tagRes.value.data  || []);
      if (userRes.status === 'fulfilled') setAllUsers(userRes.value.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (selected) {
      const updated = pubs.find(p => p.id === selected.id);
      if (updated) setSelected(updated);
    }
  }, [pubs]);

  const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Publication name required'); return; }
    setSaving(true);
    try {
      const payload = {
        name:              form.name.trim(),
        domain:            form.domain.trim(),
        site_type:         form.site_type,
        hub_name:          form.site_type === 'geo' ? form.hub_name.trim() || null : null,
        region:            form.site_type === 'geo' ? form.region.trim() || null : null,
        industry_category: form.site_type === 'industry' ? form.industry_category.trim() || null : null,
        tagline:           form.tagline.trim() || null,
        story_tags:        form.site_type === 'geo'
          ? form.story_tags_csv.split(',').map(t => t.trim()).filter(Boolean) : [],
        industry_tags:     form.site_type === 'industry'
          ? form.industry_tags_csv.split(',').map(t => t.trim()).filter(Boolean) : [],
        local_areas_csv:   form.site_type === 'geo' ? form.local_areas_csv : '',
        user_ids:          form.user_access === 'select' ? form.user_ids : [],
      };
      const res = await api.post('/publications', payload);
      toast.success('Publication created');
      setDialog(false);
      await fetchData();
      setSelected(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    if (!form.name.trim() || !selected) return;
    setSaving(true);
    try {
      await api.put(`/publications/${selected.id}`, {
        name: form.name.trim(), domain: form.domain.trim(),
      });
      toast.success('Publication updated');
      setEditing(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const handleDelete = async (pubId) => {
    if (!window.confirm('Delete this publication? This will also delete all associated tags and local areas.')) return;
    try {
      await api.delete(`/publications/${pubId}`);
      toast.success('Publication deleted');
      setSelected(null);
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = pubs.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.domain || '').toLowerCase().includes(search.toLowerCase())
  );

  const label = 'text-sm text-[#6E6258] font-medium mb-1.5 block';

  if (selected && editing) {
    return (
      <div className="space-y-6 animate-fade-in max-w-lg">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setEditing(false)}
            className="text-muted-foreground hover:text-foreground hover:bg-[#FFF4E8]">
            <ArrowLeft size={20} />
          </Button>
          <h1 className="text-2xl font-bold text-foreground font-outfit">Edit — {selected.name}</h1>
        </div>
        <Card className="bg-card border-border">
          <CardContent className="p-6 space-y-4">
            <div>
              <label className={label}>Publication Name</label>
              <Input value={form.name} onChange={e => f('name', e.target.value)}
                placeholder="e.g. NW London Today"
                className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />
            </div>
            <div>
              <label className={label}>Domain</label>
              <Input value={form.domain} onChange={e => f('domain', e.target.value)}
                placeholder="e.g. nwlondontoday.com"
                className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />
              <p className="text-xs text-muted-foreground mt-1">Must match the purchased domain exactly.</p>
            </div>
          </CardContent>
        </Card>
        <div className="flex items-center gap-2">
          <Button onClick={handleUpdate} disabled={saving} className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]">
            {saving ? <><Loader2 size={14} className="animate-spin mr-1.5" />Saving...</> : 'Save Changes'}
          </Button>
          <Button variant="ghost" onClick={() => setEditing(false)} className="text-muted-foreground hover:text-foreground">
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  if (selected) {
    return (
      <PublicationDetail
        pub={selected} allTags={allTags} allUsers={allUsers}
        canManage={canManage} canDelete={canDelete}
        onBack={() => setSelected(null)}
        onEdit={() => { setForm({ ...emptyForm, name: selected.name, domain: selected.domain || '' }); setEditing(true); }}
        onDelete={handleDelete}
        onDataChange={fetchData}
      />
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="publications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground font-outfit tracking-tight">Publications</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create and manage publications. Configure branding and launch from Site Launcher.
          </p>
        </div>
        {canManage && (
          <Button onClick={() => { setForm(emptyForm); setDialog(true); }}
            className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]" data-testid="add-publication-btn">
            <Plus size={16} className="mr-2" /> New Publication
          </Button>
        )}
      </div>

      <Input value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search publications..."
        className="max-w-sm bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <div key={i} className="h-36 bg-[#FFF4E8] border border-border rounded-xl animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <Globe className="mx-auto text-[#E7D2B8] mb-3" size={48} />
            <p className="text-muted-foreground text-lg">No publications yet</p>
            <p className="text-sm text-muted-foreground mt-1">Create your first publication to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(pub => {
            const pubTags       = allTags.filter(t => t.publication_id === pub.id);
            const assignedCount = allUsers.filter(u => (u.publication_ids || []).includes(pub.id)).length;
            const isIndustry    = pub.site_type === 'industry';
            return (
              <Card key={pub.id} onClick={() => setSelected(pub)}
                className="bg-card border-border hover:border-[#C9B49A] hover:shadow-sm transition-all cursor-pointer"
                data-testid={`pub-card-${pub.id}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-foreground font-outfit truncate">{pub.name}</CardTitle>
                  {pub.domain && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Globe size={11} /> {pub.domain}
                    </p>
                  )}
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {pub.site_type && (
                      <Badge className={`text-xs ${isIndustry ? 'bg-purple-100 text-purple-700 border-purple-200' : 'bg-blue-100 text-blue-700 border-blue-200'}`}>
                        {isIndustry ? 'Industry' : 'Geo'}
                      </Badge>
                    )}
                    {isIndustry && pub.industry_category && (
                      <span className="text-xs text-muted-foreground">{pub.industry_category}</span>
                    )}
                    {!isIndustry && pub.region && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin size={10} /> {pub.region}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {pubTags.length > 0 && (
                      <span className="flex items-center gap-1"><Tag size={11} /> {pubTags.length} tag{pubTags.length !== 1 ? 's' : ''}</span>
                    )}
                    <span className="flex items-center gap-1">
                      <Users size={11} />
                      {assignedCount > 0 ? `${assignedCount} user${assignedCount !== 1 ? 's' : ''}` : 'All users'}
                    </span>
                  </div>
                  {pubTags.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {pubTags.slice(0, 3).map(tag => (
                        <Badge key={tag.id} className="text-[10px] bg-[#F8EBD9] text-[#6E6258] border-[#E7D2B8]">{tag.name}</Badge>
                      ))}
                      {pubTags.length > 3 && (
                        <Badge className="text-[10px] bg-[#F8EBD9] text-[#9C8E84] border-[#E7D2B8]">+{pubTags.length - 3}</Badge>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground font-outfit">New Publication</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Set up the publication. Configure branding and launch from Site Launcher afterwards.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={label}>Publication Name <span className="text-red-400">*</span></label>
                <Input value={form.name} onChange={e => f('name', e.target.value)}
                  placeholder="e.g. NW London Today"
                  className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" autoFocus />
              </div>
              <div>
                <label className={label}>Domain</label>
                <Input value={form.domain} onChange={e => f('domain', e.target.value)}
                  placeholder="e.g. nwlondontoday.com"
                  className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />
              </div>
            </div>

            <div>
              <label className={label}>Site Type</label>
              <div className="flex gap-2">
                <button
                  onClick={() => { f('site_type', 'geo'); f('story_tags_csv', GEO_DEFAULT_TAGS); f('industry_tags_csv', ''); f('industry_category', ''); }}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${form.site_type === 'geo' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-border text-muted-foreground hover:bg-[#FFF4E8]'}`}
                >Geographical</button>
                <button
                  onClick={() => { f('site_type', 'industry'); f('story_tags_csv', ''); f('hub_name', ''); f('region', ''); f('local_areas_csv', ''); }}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${form.site_type === 'industry' ? 'bg-purple-50 border-purple-300 text-purple-700' : 'bg-white border-border text-muted-foreground hover:bg-[#FFF4E8]'}`}
                >Industry</button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {form.site_type === 'geo' ? (
                <div>
                  <label className={label}>Region</label>
                  <Input value={form.region} onChange={e => f('region', e.target.value)}
                    placeholder="e.g. London"
                    className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />
                  <p className="text-xs text-muted-foreground mt-1">Broader geographic area.</p>
                </div>
              ) : (
                <div>
                  <label className={label}>Industry / Sector</label>
                  <Input value={form.industry_category} onChange={e => f('industry_category', e.target.value)}
                    placeholder="e.g. Finance, Real Estate"
                    className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />
                </div>
              )}
              {form.site_type === 'geo' && (
                <div>
                  <label className={label}>Publication Hub</label>
                  <Input value={form.hub_name} onChange={e => f('hub_name', e.target.value)}
                    placeholder="e.g. Northwest London"
                    className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />
                  <p className="text-xs text-muted-foreground mt-1">Primary area this publication covers.</p>
                </div>
              )}
            </div>

            <div>
              <label className={label}>Tagline <span className="text-muted-foreground text-xs font-normal">optional</span></label>
              <Input value={form.tagline} onChange={e => f('tagline', e.target.value)}
                placeholder={form.site_type === 'geo' ? 'e.g. Local news for NW London' : 'e.g. Intelligence for finance professionals'}
                className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />
            </div>

            <div>
              <label className={label}>{form.site_type === 'geo' ? 'Editorial Story Tags' : 'Industry Tags'}</label>
              <Input
                value={form.site_type === 'geo' ? form.story_tags_csv : form.industry_tags_csv}
                onChange={e => f(form.site_type === 'geo' ? 'story_tags_csv' : 'industry_tags_csv', e.target.value)}
                placeholder={form.site_type === 'geo' ? 'Business, Politics, Crime, Community, Development' : 'e.g. Markets, Investment, Banking, Deals'}
                className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />
              <p className="text-xs text-muted-foreground mt-1">
                Comma separated. {form.site_type === 'geo' ? 'Pre-filled with standard geo tags — edit if needed.' : 'Enter tags specific to this industry.'}
              </p>
            </div>

            {form.site_type === 'geo' && (
              <div>
                <label className={label}>Local Areas <span className="text-muted-foreground text-xs font-normal">optional</span></label>
                <Input value={form.local_areas_csv} onChange={e => f('local_areas_csv', e.target.value)}
                  placeholder="Brent, Harrow, Wembley, Kilburn, Harlesden"
                  className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]" />
                <p className="text-xs text-muted-foreground mt-1">Comma separated. Add more later from the Local Areas tab.</p>
              </div>
            )}

            <div className="pt-2 border-t border-border">
              <label className={label}>Access</label>
              <div className="flex gap-2 mb-3">
                <button onClick={() => { f('user_access', 'all'); f('user_ids', []); }}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${form.user_access === 'all' ? 'bg-[#FFF4E8] border-[#C9B49A] text-[#1F1A17]' : 'bg-white border-border text-muted-foreground hover:bg-[#FFF4E8]'}`}>
                  All users
                </button>
                <button onClick={() => f('user_access', 'select')}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${form.user_access === 'select' ? 'bg-[#FFF4E8] border-[#C9B49A] text-[#1F1A17]' : 'bg-white border-border text-muted-foreground hover:bg-[#FFF4E8]'}`}>
                  Restrict access
                </button>
              </div>
              {form.user_access === 'all' && (
                <p className="text-xs text-muted-foreground">All approved users have access by default.</p>
              )}
              {form.user_access === 'select' && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground mb-2">Only selected users will have access. Admins always have full access.</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {allUsers.filter(u => u.role !== 'admin').map(u => {
                      const isSelected = form.user_ids.includes(u.id);
                      return (
                        <button key={u.id}
                          onClick={() => f('user_ids', isSelected ? form.user_ids.filter(id => id !== u.id) : [...form.user_ids, u.id])}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-left transition-colors ${isSelected ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-border hover:bg-[#FFF4E8]'}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{u.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-2">
                            <Badge className={`text-xs border capitalize ${u.role === 'editor' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`}>{u.role}</Badge>
                            {isSelected && <Check size={14} className="text-emerald-600" />}
                          </div>
                        </button>
                      );
                    })}
                    {allUsers.filter(u => u.role !== 'admin').length === 0 && (
                      <p className="text-xs text-muted-foreground italic">No non-admin users available.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)} className="text-muted-foreground hover:text-foreground">Cancel</Button>
            <Button onClick={handleCreate} disabled={saving || !form.name.trim()} className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]">
              {saving ? <><Loader2 size={14} className="animate-spin mr-1.5" />Creating...</> : 'Create Publication'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}